<template>
  <div class="timeline-container">
    <!-- Botón descargar -->
    <div class="d-flex justify-content-end mr-1">
      <b-button
        v-b-tooltip.hover
        :title="isDownloadingPdf ? 'Generando PDF...' : 'Descargar PDF'"
        size="sm"
        variant="outline-primary"
        class="d-flex align-items-center"
        :disabled="isDownloadingPdf"
        @click="downloadTimelinePdf"
      >
        <feather-icon
          icon="DownloadIcon"
          size="13"
        />
        <!-- <span style="padding-left: 3px;">
          Descargar
        </span> -->
      </b-button>
    </div>
    <!-- Botón descargar -->
    <div
      v-for="(item, index) in timelineItems"
      :key="`timeline-${index}`"
      class="timeline-item"
      :class="item.type"
    >
      <div class="timeline-item-icon-container" :class="getIconColor(item)">
        <feather-icon class="timeline-item-icon" :icon="getIcon(item)" size="15" />
      </div>
      <div class="d-flex flex-column flex-content-end w-100">
        <div
          class="timeline-item-title d-flex flex-row justify-content-between align-content-center mb-0"
          @click="toggleCollapse(index)"
        >
          <div class="align-self-center">{{ formatDate(item.date) }}</div>
          <div class="d-flex flex-row align-items-center h6 mb-0">
            <div class="text-dark d-flex align-items-center cursor-pointer">
              <strong class="me-2">{{ getTypeLabel(item) }}</strong>
              <feather-icon
                class="chevron-icon"
                :class="{ rotated: openCollapses[index] }"
                icon="ChevronDownIcon"
                size="20"
              />
            </div>
          </div>
        </div>
        <b-collapse
          :id="`collapse${index}`"
          v-model="openCollapses[index]"
          class="collapse-content"
        >
          <div class="timeline-item-content">
            <analytic-record-item v-if="item.type === 'analytic_records'" :item="item" />
            <comment-item v-else-if="item.type === 'comments'" :item="item" />
            <detection-event-item
              v-else-if="item.type === 'detection_events'"
              :item="item"
            />
            <command-log-item v-else-if="item.type === 'command_logs'" :item="item" />
            <action-plan-logs
              v-else-if="item.type === 'action_plans_logs'"
              :item="item"
            />
            <alarm-bridge-log-device-item
              v-else-if="item.type === 'alarm_bridge_device_logs'"
              :item="item"
            />
            <alarm-workflow-item v-else-if="item.type === 'alarm_workflow'" :item="item" />
          </div>
        </b-collapse>
      </div>
    </div>

    <div
      v-if="!timelineItems || timelineItems.length === 0"
      class="text-center text-muted p-4"
    >
      <p>{{ $t("alarm.timeline.no_items") }}</p>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import {useUtils as useI18nUtils} from "@core/libs/i18n";
import {useToast} from "vue-toastification/composition";
import axios from "@axios";
import { BCollapse } from "bootstrap-vue";
import store from "@/store";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import AnalyticRecordItem from "./AnalyticRecordItem.vue";
import CommentItem from "./CommentItem.vue";
import DetectionEventItem from "./DetectionEventItem.vue";
import CommandLogItem from "./CommandLogItem.vue";
import ActionPlanLogs from "./ActionPlanLogs.vue";
import AlarmBridgeLogDeviceItem from "./AlarmBridgeLogDeviceItem.vue";
import AlarmWorkflowItem
  from "@/views/components/GridView/layouts/slots/InfoSlot/Timeline/AlarmWorkflowItem.vue";

export default {
  name: "Timeline",
  components: {
    BCollapse,
    AnalyticRecordItem,
    CommentItem,
    DetectionEventItem,
    CommandLogItem,
    ActionPlanLogs,
    AlarmBridgeLogDeviceItem,
    AlarmWorkflowItem,
  },
  props: {
    idAlarm: {
      type: Number,
      required: true,
    },
    alarmData: {
      type: Object,
      required: true,
    },
    dataVersion: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const timelineData = ref(null);
    const openCollapses = ref({});
    const isDownloadingPdf = ref(false);

    const openImageModal = async (imageUrl) => {
      await store.dispatch("image_modal/imageUrl", imageUrl);
    };

    const timelineItems = computed(() => {
      if (!timelineData.value || !timelineData.value.data) return [];
      return timelineData.value.data.filter((item) => shouldShowItem(item));
    });

    const shouldShowItem = (item) => {
      if (!item || !item.data) return false;

      // Only show items of the 5 supported types
      switch (item.type) {
        case "analytic_records":
          return item.data && Object.keys(item.data).length > 0;
        case "comments":
          return item.data.comment && item.data.comment.trim() !== "";
        case "detection_events":
          return item.data && Object.keys(item.data).length > 0;
        case "command_logs":
          return item.data && Object.keys(item.data).length > 0;
        case "action_plans_logs":
          return item.data.history && item.data.history.length > 0;
        case "alarm_bridge_device_logs":
          return item.data && Object.keys(item.data).length > 0;
        case "alarm_workflow":
          return item.data && Object.keys(item.data).length > 0;
        default:
          return false;
      }
    };

    const toggleCollapse = (index) => {
      openCollapses.value = {
        ...openCollapses.value,
        [index]: !openCollapses.value[index],
      };
    };

    const fetchAlarmTimeline = async () => {
      try {
        const response = await axios.get(`/v1/alarm/${props.idAlarm}/timeline`);
        timelineData.value = response.data;

        // Initialize openCollapses state
        if (timelineData.value && timelineData.value.data) {
          const initialState = {};
          const filteredItems = timelineData.value.data.filter((item) =>
            shouldShowItem(item)
          );
          filteredItems.forEach((_, index) => {
            initialState[index] = false; // All closed by default
          });
          openCollapses.value = initialState;
        }
      } catch (error) {
        timelineData.value = null;
        console.error("Error fetching alarm timeline:", error);
      }
    };

    const getDownloadFilename = (contentDisposition) => {
      const utf8Match = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
      const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
      const encodedFilename = utf8Match?.[1] ?? filenameMatch?.[1];

      if (!encodedFilename) return `timeline-alarma-${props.idAlarm}.pdf`;

      try {
        return decodeURIComponent(encodedFilename);
      } catch {
        return encodedFilename;
      }
    };

    const downloadTimelinePdf = async () => {
      if (isDownloadingPdf.value) return;

      isDownloadingPdf.value = true;

      try {
        const response = await axios.get(`/v1/alarm/${props.idAlarm}/timeline/pdf`, {
          responseType: "blob",
          headers: { Accept: "application/pdf" },
        });
        const filename = getDownloadFilename(response.headers["content-disposition"]);
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const link = document.createElement("a");

        try {
          link.href = url;
          link.download = filename;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
        } finally {
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Error downloading alarm timeline PDF:", error);
        toast({
          component: ToastificationContent,
          props: {
            title: "No se pudo descargar el timeline de la alarma",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isDownloadingPdf.value = false;
      }
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString("es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const getTypeLabel = (item) => {

      if (item.type === 'alarm_bridge_device_logs') {
        return item.data.title;
      }

      const labels = {
        analytic_records: t("alarm.timeline.analytic_record"),
        comments: t("alarm.timeline.comment"),
        detection_events: t("alarm.timeline.detection_event"),
        command_logs: t("alarm.timeline.command_log"),
        action_plans_logs: t("alarm.timeline.action_plans_logs"),
        alarm_bridge_device_logs: t("alarm.timeline.alarm_bridge_device_logs"),
        alarm_workflow: t("alarm.timeline.alarm_workflow"),
        alarm_workflow_MANUAL_ALARM_DISPATCHED: 'Alarma activada - ' + t("alarm.timeline.alarm_workflow"),
        alarm_workflow_MANUAL_ALARM_RESTORED: 'Alarma restaurada - ' + t("alarm.timeline.alarm_workflow"),
        alarm_workflow_SENT_TO_MAINTENANCE: 'Alarma en mantenimiento - ' + t("alarm.timeline.alarm_workflow"),
        alarm_workflow_RESOLVED_MAINTENANCE: 'Alarma fuera de mantenimiento - ' + t("alarm.timeline.alarm_workflow"),
      };

      const type = item.type;
      const action = item.data.action?.action;

      return labels[`${type}_${action}`] || labels[type] || type;
    };

    const getIconColor = (item) => {
      const type = item.type;

      if (type === 'alarm_workflow' && item.data?.action?.action) {
        const action = item.data.action.action;
        if (action === 'TAKEN') {
          return 'red';
        } else if (action === 'RELEASED') {
          return 'blue';
        } else if (action === 'SENT_TO_REVIEW') {
          return 'orange';
        } else if (action === 'CHANGES_REQUESTED') {
          return 'yellow';
        } else if (action === 'SUPERVISOR_TAKEN_FROM_POOL') {
          return 'red';
        } else if (action === 'OPERATOR_RESUMED_WORK') {
          return 'red';
        } else if (action === 'COMPLETED') {
          return 'green';
        } else if (action === 'MANUAL_ALARM_DISPATCHED') {
          return 'purple';
        } else if (action === 'MANUAL_ALARM_RESTORED') {
          return 'green-light';
        } else if (action === 'SENT_TO_MAINTENANCE') {
          return 'yellow';
        } else if (action === 'RESOLVED_MAINTENANCE') {
          return 'green-light';
        }
      }

      const colors = {
        analytic_records: "blue",
        comments: "green",
        detection_events: "yellow",
        command_logs: "grey",
        action_plans_logs: "purple",
        alarm_bridge_device_logs: "grey",
      };
      return colors[type] || "grey";
    };

    const getIcon = (item) => {
      const icons = {
        analytic_records: "CameraIcon",
        comments: "MessageSquareIcon",
        detection_events: "AlertTriangleIcon",
        command_logs: "TerminalIcon",
        action_plans_logs: "ClipboardIcon",
        alarm_bridge_device_logs: "BoxIcon",
        alarm_workflow: "UserIcon",
        alarm_workflow_MANUAL_ALARM_DISPATCHED: "BellIcon",
        alarm_workflow_MANUAL_ALARM_RESTORED: "BellOffIcon",
        alarm_workflow_SENT_TO_MAINTENANCE: "ToolIcon",
        alarm_workflow_RESOLVED_MAINTENANCE: "ToolIcon",
      };
      const type = item.type;
      const action = item.data.action?.action;

      return icons[`${type}_${action}`] || icons[type] || "HelpCircleIcon";
    };

    onMounted(() => {
      fetchAlarmTimeline();
    });

    return {
      timelineData,
      timelineItems,
      openCollapses,
      isDownloadingPdf,
      shouldShowItem,
      downloadTimelinePdf,
      toggleCollapse,
      formatDate,
      getTypeLabel,
      getIconColor,
      getIcon,
      openImageModal,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.timeline-container {
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: "Inter", sans-serif;
  margin-bottom: 2rem;
}

.timeline-item {
  position: relative;
  display: flex;
  flex-direction: row;
  padding-block: 0.5rem;
  gap: 0.5rem;
  align-content: center;
}

.timeline-item::before {
  content: "";
  position: absolute;
  top: 1rem;
  left: 1.1rem;
  transform: translateX(-50%);
  width: 2px;
  height: 100%;
  background-color: #e9ecef;
  z-index: 1;
}

.timeline-item:last-child::before {
  display: none;
}

.timeline-item-icon-container {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 0.4rem;
  height: fit-content;
  min-width: 2.2rem;
  min-height: 2.2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.timeline-item-icon {
  color: white;
}

.timeline-item-title {
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.timeline-item-title:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.timeline-item-content {
  width: 100%;
  margin-top: 0rem;
}

.chevron-icon {
  transition: transform 0.3s ease;
}

.collapse-content {
  .dark-layout & {
    background-color: $third-dark !important;
  }
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

.cursor-pointer {
  cursor: pointer;
}

.w-100 {
  width: 100%;
}

.shadow-sm {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

// Colores de iconos
.blue {
  background-color: #007bff;
}
.grey {
  background-color: #6c757d;
}
.green {
  background-color: #28a745;
}
.yellow {
  background-color: #ffc107;
}
.purple {
  background-color: #6f42c1;
}
.orange {
  background-color: #fd7e14;
}
.cyan {
  background-color: #17a2b8;
}
.red {
  background-color: #ea5455;
}
.purple {
  background-color: #ff0000;
}
.green-light {
  background-color: #00ba28;
}
</style>
